import { AfterViewInit, Component, ElementRef, HostBinding, Input, OnDestroy, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import flatpickr from 'flatpickr';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ]
})
export class DatePickerComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder = 'Select date';
  @Input() inputId?: string;

  @Input()
  @HostBinding('class.compact')
  compact = false;

  @Input()
  @HostBinding('class.invalid')
  invalid = false;

  @ViewChild('inputEl', { static: true }) private readonly inputRef!: ElementRef<HTMLInputElement>;

  protected disabled = false;

  private instance?: FlatpickrInstance;
  private pendingValue: string | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    this.instance = flatpickr(this.inputRef.nativeElement, {
      dateFormat: 'Y-m-d',
      altInput: true,
      altInputClass: 'date-picker__display-input',
      altFormat: 'j M Y',
      disableMobile: true,
      onChange: (_dates, dateStr) => {
        this.onChange(dateStr);
      },
      onClose: () => {
        this.onTouched();
      }
    });

    if (this.pendingValue !== null) {
      this.instance.setDate(this.pendingValue, false);
    }

    if (this.disabled) {
      this.instance.set('clickOpens', false);
    }

    if (this.inputId && this.instance.altInput) {
      this.instance.altInput.id = this.inputId;
    }
  }

  ngOnDestroy(): void {
    this.instance?.destroy();
  }

  writeValue(value: string | null): void {
    if (this.instance) {
      this.instance.setDate(value ?? '', false);
    } else {
      this.pendingValue = value ?? '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.instance?.set('clickOpens', !isDisabled);
    this.instance?.altInput?.toggleAttribute('disabled', isDisabled);
  }
}
